import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useState, useEffect } from "react";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice/snackbarSlice";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById } from "../../methods/user/userRequests";
import { getError } from "../../helpers/form/errorHelper";
import ROLES from "../../enums/Roles";
import { relationshipOptions } from "../../enums/Relationships";
import { resolveMediaUrl } from "../../utils/backendUrl";
import ResourceCrudPanel from "../crm/ResourceCrudPanel";
import { contractResources } from "../../pages/crm/ContractsPage";
import LocalContractSignDialog from "../contracts/LocalContractSignDialog";
import ContractDocumentsPreviewDialog from "../contracts/ContractDocumentsPreviewDialog";
import SepaMandateDialog from "../contracts/SepaMandateDialog";
import ContractFormFields from "../contracts/ContractFormFields";
import {
  buildContractPayload,
  createDefaultContractForm,
  mapContractRowToForm,
} from "../contracts/contractFormConfig";

const formatLabel = (text) => {
  if (!text) return "";
  return text
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
};

const normalizeRows = (response) => {
  if (!response?.success) return [];
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const toDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if (value.name) return value.name;
    if (value.id) return value.id;
    return JSON.stringify(value);
  }
  return String(value);
};

const getExerciseMediaAssets = (exercise) => {
  const mediaAssets = Array.isArray(exercise?.media_assets)
    ? exercise.media_assets
    : (Array.isArray(exercise?.mediaAssets) ? exercise.mediaAssets : []);

  if (!mediaAssets.length) return [];

  const variantRank = {
    original: 0,
    large: 1,
    medium: 2,
    small: 3,
    thumb: 4,
  };

  return [...mediaAssets].sort((a, b) => {
    const primaryDiff = Number(Boolean(b?.is_primary)) - Number(Boolean(a?.is_primary));
    if (primaryDiff !== 0) return primaryDiff;

    const variantA = String(a?.variant || "").toLowerCase();
    const variantB = String(b?.variant || "").toLowerCase();
    const rankA = variantRank[variantA] ?? 10;
    const rankB = variantRank[variantB] ?? 10;
    if (rankA !== rankB) return rankA - rankB;

    return String(a?.created_at || "").localeCompare(String(b?.created_at || ""));
  });
};

const renderTrainingMediaGallery = (exercise) => {
  const mediaAssets = getExerciseMediaAssets(exercise);

  if (!mediaAssets.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No media
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {mediaAssets.map((media, index) => {
        const fallbackStoragePathUrl = media?.storage_path
          ? `/storage/${String(media.storage_path).replace(/^\/+/, "")}`
          : "";
        const resolvedMediaUrl = resolveMediaUrl(fallbackStoragePathUrl || media?.public_url);
        const versionToken = String(media?.updated_at || media?.id || "").trim();
        const mediaUrl = resolvedMediaUrl && versionToken
          ? `${resolvedMediaUrl}${resolvedMediaUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(versionToken)}`
          : resolvedMediaUrl;

        if (!mediaUrl) {
          return null;
        }

        const mediaType = String(media?.media_type || "").toLowerCase();
        const mimeType = String(media?.file_mime || "").toLowerCase();
        const isVideo = mediaType === "video" || mimeType.startsWith("video/");

        return (
          <Box key={`${media?.id || media?.storage_path || index}`} sx={{ minWidth: 120, maxWidth: 220 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {String(media?.variant || "original")} {media?.is_primary ? "· primary" : ""}
            </Typography>
            {isVideo ? (
              <video
                src={mediaUrl}
                controls
                preload="metadata"
                style={{
                  width: "100%",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            ) : (
              <img
                src={mediaUrl}
                alt={`${exercise?.name || "Exercise"} media ${index + 1}`}
                style={{
                  width: "100%",
                  height: 120,
                  objectFit: "cover",
                  borderRadius: 8,
                  display: "block",
                }}
                loading="lazy"
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const UserProfile = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [lookups, setLookups] = useState({});
  const [errors, setErrors] = useState({});
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState("");
  const [relatedData, setRelatedData] = useState({
    classes: {},
    inventory: {},
    assets: {},
    catalog: {},
    sales: {},
    access: {},
    training: {},
  });

  const [employees, setEmployees] = useState([]);
  const [gymUsersOptions, setGymUsersOptions] = useState([]);
  const [contractPlans, setContractPlans] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsError, setContractsError] = useState("");
  const [contracts, setContracts] = useState([]);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractSaving, setContractSaving] = useState(false);
  const [contractInlineTab, setContractInlineTab] = useState(0);
  const [contractForm, setContractForm] = useState(createDefaultContractForm);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signTargetContract, setSignTargetContract] = useState(null);
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [signError, setSignError] = useState("");
  const [signAcceptedTerms, setSignAcceptedTerms] = useState(false);
  const [signSepa, setSignSepa] = useState(true);
  const [signPreviewLoading, setSignPreviewLoading] = useState(false);
  const [signPreviewError, setSignPreviewError] = useState("");
  const [signContractPreviewUrl, setSignContractPreviewUrl] = useState("");
  const [signSepaPreviewUrl, setSignSepaPreviewUrl] = useState("");
  const [signAdditionalPreviewDocs, setSignAdditionalPreviewDocs] = useState([]);
  const [signPreviewType, setSignPreviewType] = useState("contract");
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTargetContract, setPreviewTargetContract] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewContractUrl, setPreviewContractUrl] = useState("");
  const [previewSepaUrl, setPreviewSepaUrl] = useState("");
  const [previewAdditionalPreviewDocs, setPreviewAdditionalPreviewDocs] = useState([]);
  const [previewType, setPreviewType] = useState("contract");
  const [sepaDialogOpen, setSepaDialogOpen] = useState(false);
  const [sepaDialogMode, setSepaDialogMode] = useState("setup");
  const [sepaTargetContract, setSepaTargetContract] = useState(null);
  const [sepaSubmitting, setSepaSubmitting] = useState(false);
  const [sepaError, setSepaError] = useState("");
  const [sepaForm, setSepaForm] = useState({
    iban: "",
    account_holder_name: "",
    email: "",
    reason: "",
  });
  const canvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);
  const hasSignatureRef = React.useRef(false);

  const [user, setUser] = useState({
    name: "",
    email: "",
    role: ROLES.CUSTOMER,
    password: "",
    is_active: 1,
    gym_user_info: {
      // campos usados no formulário + alinhados com o backend
      full_name: "",
      preferred_name: "",
      initials: "",
      birth_date: "",
      nif: "",
      document_number: "",
      document_expiry: "",
      start_date: "",
      end_date: "",
      title_id: "",
      job_position_id: "",
      job_department_id: "",
      job_company_name: "",
      job_company_address: "",
      job_company_phone: "",
      job_employement_country: "",
      job_profession: "",
      gender_id: "",
      civil_status_id: "",
      document_type_id: "",
      nationality_id: "",
      manager_id: null,
      professor_id: null,
      is_athlete: 0,
      athlete_modality: null,
      invoicing_nif: "",
      invoicing_name: "",
      invoicing_address: "",
      notes: "",
    },
    contact: {
      address: "",
      url: "",
      city: "",
      postal_code: "",
      country_id: "",
      phone: "",
      mobile: "",
      emergency_contact: "",
      father_name: "",
      father_contact: "",
      mother_name: "",
      mother_contact: "",
    },
    marketing_info: {
      receive_sms: 0,
      receive_email: 0,
      allow_marketing: 0,
      image_consent: 0,
      show_in_program: 0,
      show_in_app: 0,
      visit_date: "",
      complete_visit: 0,
      last_contact: "",
      actual_action: "",
      next_contact: "",
      next_action: "",
      campgain_id: "",
      source_id: "",
      interests: "",
    },
    medical_info: {
      health_number: "",
      healthcare_system: "",
      insurance: 0,
      has_medical_issues: 0,
      medical_authorization: 0,
      notes: "",
    },
  });

  const [createdRelationships, setCreatedRelationships] = useState([]);
  const [deletedRelationships, setDeletedRelationships] = useState([]);

  const UserRoleOptions = [
    {
      id: ROLES.CUSTOMER,
      name: ROLES.CUSTOMER,
    },
  ];

  // helper para ler o value correto mesmo em nested (contact, etc)
  const getFieldValue = (section, field) => {
    if (section === "root") {
      return user?.[field] ?? "";
    }

    let obj = user;
    const parts = section.split(".");
    for (const p of parts) {
      if (!obj || typeof obj !== "object") return "";
      obj = obj[p];
    }

    return obj?.[field] ?? "";
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [lookupsRes, userRes, employeesRes, gymUsersRes, contractPlansRes] =
          await Promise.all([
            callBackendApi("/lookups", "GET"),
            id ? getUserById(id) : Promise.resolve({ success: false }),
            callBackendApi("/user/employees-lookup", "GET"),
            callBackendApi("/user/gym-users-lookup", "GET"),
            callBackendApi("/contract-plans", "GET"),
          ]);

        if (lookupsRes.success) setLookups(lookupsRes.data.data);
        if (employeesRes.success) setEmployees(employeesRes.data || []);
        if (gymUsersRes.success) setGymUsersOptions(gymUsersRes.data || []);
        if (contractPlansRes.success) {
          const activePlans = normalizeRows(contractPlansRes).filter((row) =>
            ["published", "active"].includes(String(row?.status || "").toLowerCase())
          );
          setContractPlans(activePlans);
        }

        if (id && userRes.success) {
          const apiUser = userRes.data;

          const normalized = {
            ...user, // defaults
            ...apiUser,
            gym_user_info: {
              ...user.gym_user_info,
              ...(apiUser.gym_user_info || {}),
            },
            contact: {
              ...user.contact,
              ...(apiUser.contact || {}),
            },
            marketing_info: {
              ...user.marketing_info,
              ...(apiUser.marketing_info || {}),
            },
            medical_info: {
              ...user.medical_info,
              ...(apiUser.medical_info || {}),
            },
          };

          setUser(normalized);

          setCreatedRelationships(apiUser.relationships || []);
          setDeletedRelationships([]);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const loadContracts = async () => {
      if (!id) return;

      setContractsLoading(true);
      setContractsError("");
      const response = await callBackendApi("/contracts", "GET");
      if (!response?.success) {
        setContracts([]);
        setContractsError("Failed to load contracts.");
        setContractsLoading(false);
        return;
      }

      const userContracts = normalizeRows(response).filter(
        (row) => String(row.user_id || "") === String(id)
      );
      setContracts(userContracts);

      setContractsLoading(false);
    };

    loadContracts();
  }, [id]);

  useEffect(() => {
    const loadRelatedData = async () => {
      if (!id) return;

      setRelatedLoading(true);
      setRelatedError("");

      try {
        const [
          classBookingsRes,
          stockMovementsRes,
          maintenanceLogsRes,
          incidentsRes,
          saleRes,
          saleLinesRes,
          salePaymentsRes,
          saleRefundsRes,
          memberEntitlementsRes,
          entitlementMovementsRes,
          trainingAssignmentsRes,
        ] = await Promise.all([
          callBackendApi("/class-bookings", "GET"),
          callBackendApi("/inventory-stock-movements", "GET"),
          callBackendApi("/asset-maintenance-logs", "GET"),
          callBackendApi("/asset-incidents", "GET"),
          callBackendApi("/sales", "GET"),
          callBackendApi("/sale-lines", "GET"),
          callBackendApi("/sale-payments", "GET"),
          callBackendApi("/sale-refunds", "GET"),
          callBackendApi("/member-entitlements", "GET"),
          callBackendApi("/entitlement-movements", "GET"),
          callBackendApi(`/training-plan-assignments?gym_user_id=${id}&include_plan_structure=1&include_expired=1`, "GET"),
        ]);

        const classBookings = normalizeRows(classBookingsRes).filter(
          (row) => String(row.user_id || "") === String(id)
        );

        const inventoryMovements = normalizeRows(stockMovementsRes).filter(
          (row) => String(row.created_by_user_id || "") === String(id)
        );

        const maintenanceLogs = normalizeRows(maintenanceLogsRes).filter(
          (row) => String(row.created_by_user_id || "") === String(id)
        );

        const incidents = normalizeRows(incidentsRes).filter(
          (row) => String(row.reported_by_user_id || "") === String(id)
        );

        const sales = normalizeRows(saleRes).filter(
          (row) =>
            String(row.user_id || "") === String(id) ||
            String(row.sold_by_user_id || "") === String(id)
        );

        const saleIds = new Set(sales.map((sale) => sale.id));
        const saleLines = normalizeRows(saleLinesRes).filter((row) =>
          saleIds.has(row.sale_id)
        );
        const salePayments = normalizeRows(salePaymentsRes).filter((row) =>
          saleIds.has(row.sale_id)
        );
        const saleRefunds = normalizeRows(saleRefundsRes).filter(
          (row) =>
            saleIds.has(row.sale_id) || String(row.refunded_by_user_id || "") === String(id)
        );

        const memberEntitlements = normalizeRows(memberEntitlementsRes).filter(
          (row) => String(row.user_id || "") === String(id)
        );
        const entitlementIds = new Set(memberEntitlements.map((entitlement) => entitlement.id));
        const entitlementMovements = normalizeRows(entitlementMovementsRes).filter((row) =>
          entitlementIds.has(row.entitlement_id)
        );

        const trainingAssignments = normalizeRows(trainingAssignmentsRes).filter(
          (row) => String(row.gym_user_id || "") === String(id)
        );
        const activeTrainingAssignments = trainingAssignments.filter((row) => {
          const status = String(row.status || "").toLowerCase();
          return status === "active" || status === "paused";
        });

        setRelatedData({
          classes: {
            class_bookings: classBookings,
          },
          inventory: {
            stock_movements: inventoryMovements,
          },
          assets: {
            maintenance_logs: maintenanceLogs,
            incidents,
          },
          catalog: {
            purchased_products: saleLines,
          },
          sales: {
            sales,
            sale_lines: saleLines,
            sale_payments: salePayments,
            sale_refunds: saleRefunds,
          },
          access: {
            member_entitlements: memberEntitlements,
            entitlement_movements: entitlementMovements,
          },
          training: {
            assignments: trainingAssignments,
            active_assignments: activeTrainingAssignments,
          },
        });
      } catch (error) {
        setRelatedError("Failed to load related information.");
      } finally {
        setRelatedLoading(false);
      }
    };

    loadRelatedData();
  }, [id]);

  const handleChange = (section, field, value) => {
    setUser((prev) => {
      const updated = { ...prev };
      if (section === "root") {
        updated[field] = value;
      } else if (section.includes(".")) {
        const [main, sub] = section.split(".");
        updated[main] = {
          ...updated[main],
          [sub]: { ...updated[main]?.[sub], [field]: value },
        };
      } else {
        updated[section] = { ...updated[section], [field]: value };
      }
      return updated;
    });
  };

  const addRelationship = () => {
    setCreatedRelationships((prev) => [
      ...prev,
      {
        id: null,
        related_user_id: "",
        connection: "",
        starting_date: "",
        end_date: "",
      },
    ]);
  };

  const handleRelationshipChange = (index, field, value) => {
    setCreatedRelationships((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleRelationshipDelete = (index) => {
    setCreatedRelationships((prev) => {
      const rel = prev[index];

      if (rel?.id) {
        setDeletedRelationships((prevDeleted) => [...prevDeleted, rel.id]);
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  const mapRelationshipsForPayload = (rels) =>
    (rels || []).map((r) => ({
      ...(r.id ? { id: r.id } : {}),
      related_user_id: r.related_user_id || null,
      connection: r.connection,
      starting_date: r.starting_date || null,
      end_date: r.end_date || null,
    }));

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});

    try {
      const method = id ? "PUT" : "POST";
      const url = id ? `/users/${id}` : "/users";

      const payload = {
        ...user,
        created_relationships: mapRelationshipsForPayload(createdRelationships),
        deleted_relationships: deletedRelationships,
      };

      const res = await callBackendApi(url, method, payload);

      if (res.success) {
        dispatch(
          showSnackbar({
            message: id
              ? "User updated successfully!"
              : "User created successfully!",
          })
        );
      } else {
        setErrors(res?.error || {});
        dispatch(showSnackbar({ message: "Error saving user." }));
      }
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: "Unexpected error while saving." }));
    } finally {
      setSaving(false);
    }
  };

  const renderSelect = (label, section, field, list = []) => {
    const errorPath = section === "root" ? field : `${section}.${field}`;

    return (
      <TextField
        select
        fullWidth
        label={formatLabel(label || field)}
        value={String(getFieldValue(section, field) ?? "")}
        onChange={(e) =>
          handleChange(section, field, e.target.value ? e.target.value : "")
        }
        helperText={getError(errors, errorPath)}
        error={!!getError(errors, errorPath)}
      >
        <MenuItem value="">-- Select --</MenuItem>
        {(list || []).map((item) => (
          <MenuItem key={item.id} value={String(item.id)}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const renderSwitch = (label, section, field, checked) => (
    <FormControlLabel
      control={
        <Switch
          checked={!!checked}
          onChange={(e) =>
            handleChange(section, field, e.target.checked ? 1 : 0)
          }
        />
      }
      label={label}
    />
  );

  const renderRelationTable = (title, rows, columns) => (
    <Accordion key={title} disableGutters sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">
          {title} ({rows.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No records found.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={`${title}-${col}`}>{formatLabel(col)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${title}-${row.id || index}`}>
                  {columns.map((col) => (
                    <TableCell key={`${title}-${row.id || index}-${col}`}>
                      {toDisplay(row[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );

  const renderRelatedTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Related Information
        </Typography>
        {relatedError ? <Alert severity="warning" sx={{ mb: 2 }}>{relatedError}</Alert> : null}
        {relatedLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Classes</Typography>
            {renderRelationTable("Class Bookings", relatedData.classes.class_bookings || [], [
              "class_session_id",
              "status",
              "booked_at",
              "cancelled_at",
              "checked_in_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Inventory</Typography>
            {renderRelationTable("Stock Movements", relatedData.inventory.stock_movements || [], [
              "movement_type",
              "qty",
              "unit_cost",
              "reason",
              "created_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Assets</Typography>
            {renderRelationTable("Maintenance Logs", relatedData.assets.maintenance_logs || [], [
              "asset_id",
              "log_type",
              "occurred_at",
              "cost",
            ])}
            {renderRelationTable("Asset Incidents", relatedData.assets.incidents || [], [
              "asset_id",
              "status",
              "severity",
              "reported_at",
              "resolved_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Catalog</Typography>
            {renderRelationTable("Purchased Products (Sale Lines)", relatedData.catalog.purchased_products || [], [
              "sale_id",
              "product_id",
              "product_name_snapshot",
              "qty",
              "unit_price",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Sales</Typography>
            {renderRelationTable("Sales", relatedData.sales.sales || [], [
              "channel",
              "status",
              "total",
              "currency",
              "occurred_at",
            ])}
            {renderRelationTable("Sale Payments", relatedData.sales.sale_payments || [], [
              "sale_id",
              "method",
              "amount",
              "paid_at",
            ])}
            {renderRelationTable("Sale Refunds", relatedData.sales.sale_refunds || [], [
              "sale_id",
              "status",
              "total_refunded",
              "occurred_at",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Access</Typography>
            {renderRelationTable("Member Entitlements", relatedData.access.member_entitlements || [], [
              "product_id",
              "access_rule_id",
              "status",
              "starts_at",
              "ends_at",
            ])}
            {renderRelationTable("Entitlement Movements", relatedData.access.entitlement_movements || [], [
              "entitlement_id",
              "movement_type",
              "quantity",
              "reference_type",
              "reference_id",
            ])}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Training Plans</Typography>
            {(relatedData.training.assignments || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No training assignments found.
              </Typography>
            ) : (
              <Grid container spacing={1.5} sx={{ mb: 1 }}>
                {(relatedData.training.assignments || []).map((assignment, index) => (
                  <Grid item xs={12} md={6} key={`training-assignment-summary-${assignment.id || index}`}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle2">
                          {assignment?.training_plan?.name || `Plan ${assignment?.training_plan_id || "-"}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Trainer: {assignment?.trainer?.name || assignment?.trainer_user_id || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: {toDisplay(assignment?.status)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {toDisplay(assignment?.start_date)} → {toDisplay(assignment?.valid_until)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {(relatedData.training.active_assignments || []).map((assignment, index) => (
              <Accordion key={`training-plan-structure-${assignment.id || index}`} disableGutters sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {assignment?.training_plan?.name || "Training Plan"} - Structure
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Array.isArray(assignment?.training_plan?.days) && assignment.training_plan.days.length > 0 ? (
                    assignment.training_plan.days.map((day) => (
                      <Box key={`${assignment.id}-${day.id}`} sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Week {day.week_number}, Day {day.day_number} {day.title ? `- ${day.title}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          {day.focus || "No focus defined"}
                        </Typography>
                        {(day.day_exercises || []).length > 0 ? (
                          <Grid container spacing={1.5}>
                            {(day.day_exercises || []).map((dayExercise) => (
                              <Grid item xs={12} md={6} key={`${assignment.id}-${day.id}-${dayExercise.id}`}>
                                <Card variant="outlined">
                                  <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                      {dayExercise?.exercise?.name || dayExercise.exercise_id}
                                    </Typography>
                                    <Box sx={{ mb: 1.25 }}>
                                      {renderTrainingMediaGallery(dayExercise?.exercise)}
                                    </Box>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Sets</Typography>
                                        <Typography variant="body2">{toDisplay(dayExercise.sets)}</Typography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Reps</Typography>
                                        <Typography variant="body2">{toDisplay(dayExercise.reps)}</Typography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Rest (s)</Typography>
                                        <Typography variant="body2">{toDisplay(dayExercise.rest_seconds)}</Typography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Tempo</Typography>
                                        <Typography variant="body2">{toDisplay(dayExercise.tempo)}</Typography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">RPE</Typography>
                                        <Typography variant="body2">{toDisplay(dayExercise.rpe)}</Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Notes</Typography>
                                        <Typography variant="body2">{toDisplay(dayExercise.notes)}</Typography>
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No exercises in this day.</Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No plan structure available.</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );

  const openCreateContractDialog = () => {
    setEditingContract(null);
    setContractInlineTab(0);
    setContractForm(createDefaultContractForm());
    setContractDialogOpen(true);
  };

  const openEditContractDialog = (row) => {
    setEditingContract(row);
    setContractInlineTab(0);
    setContractForm(mapContractRowToForm(row));
    setContractDialogOpen(true);
  };

  const closeContractDialog = () => {
    setContractDialogOpen(false);
    setEditingContract(null);
    setContractInlineTab(0);
  };

  const reloadContracts = async () => {
    if (!id) return;
    setContractsLoading(true);
    setContractsError("");
    const response = await callBackendApi("/contracts", "GET");
    if (!response?.success) {
      setContracts([]);
      setContractsError("Failed to load contracts.");
      setContractsLoading(false);
      return;
    }

    const userContracts = normalizeRows(response).filter(
      (row) => String(row.user_id || "") === String(id)
    );
    setContracts(userContracts);

    setContractsLoading(false);
  };

  const saveContract = async () => {
    if (!id) return;
    setContractSaving(true);
    const payload = buildContractPayload(contractForm, id);

    const endpoint = editingContract?.id ? `/contracts/${editingContract.id}` : "/contracts";
    const method = editingContract?.id ? "PUT" : "POST";
    const response = await callBackendApi(endpoint, method, payload);

    if (response?.success) {
      dispatch(showSnackbar({ message: editingContract ? "Contract updated." : "Contract created." }));
      closeContractDialog();
      reloadContracts();
    } else {
      dispatch(showSnackbar({ message: "Failed to save contract." }));
    }

    setContractSaving(false);
  };

  const deleteContract = async (row) => {
    const contractId = row?.id;
    if (!contractId) return;

    const confirmed = window.confirm(`Delete contract ${row?.contract_number || contractId}?`);
    if (!confirmed) return;

    const response = await callBackendApi(`/contracts/${contractId}`, "DELETE");
    if (response?.success) {
      dispatch(showSnackbar({ message: "Contract deleted." }));
      reloadContracts();
      return;
    }

    dispatch(showSnackbar({ message: "Failed to delete contract." }));
  };

  const getResponseErrorMessage = (response, fallbackMessage) => {
    const validationErrors = response?.validationErrors;
    if (validationErrors && typeof validationErrors === "object") {
      const firstKey = Object.keys(validationErrors)[0];
      const firstError = firstKey ? validationErrors[firstKey] : null;
      if (Array.isArray(firstError) && typeof firstError[0] === "string" && firstError[0].trim() !== "") {
        return firstError[0];
      }
      if (typeof firstError === "string" && firstError.trim() !== "") {
        return firstError;
      }
    }

    return response?.error || fallbackMessage;
  };

  const fetchLatestSepaMandate = async (contractId) => {
    const response = await callBackendApi(`/sepa-mandates?contract_id=${contractId}`, "GET");
    if (!response?.success) return null;
    const rows = normalizeRows(response);
    if (!rows.length) return null;

    const mandateWithIban = rows.find((item) => {
      const rawIban = typeof item?.iban === "string" ? item.iban : "";
      const normalized = rawIban.trim().toUpperCase();
      return normalized !== "" && normalized !== "N/A";
    });

    return mandateWithIban || rows[0] || null;
  };

  const setupSepaMandate = async (row, options = {}) => {
    const silent = Boolean(options?.silent);
    const skipSignatureCheck = Boolean(options?.skipSignatureCheck);
    const skipReload = Boolean(options?.skipReload);
    const contractId = row?.id;
    if (!contractId) return { success: false };

    if (!skipSignatureCheck && !row?.signature_date) {
      dispatch(showSnackbar({ message: "Contract must be digitally signed before SEPA setup." }));
      return { success: false, blocked: true };
    }

    const latestMandate = await fetchLatestSepaMandate(contractId);
    const defaultIban = typeof latestMandate?.iban === "string" && latestMandate.iban !== "N/A"
      ? latestMandate.iban
      : "";
    const defaultHolderName = latestMandate?.account_holder_name || row?.user?.name || user?.name || "";
    const defaultEmail = latestMandate?.payer?.email || row?.user?.email || user?.email || "";

    let iban = defaultIban;
    if (!iban && !silent) {
      iban = window.prompt("IBAN do cliente (SEPA):", defaultIban) || "";
    }
    if (!iban) {
      const message = "IBAN em falta. Não foi possível submeter o mandato SEPA à Stripe.";
      if (!silent) {
        dispatch(showSnackbar({ message }));
      }
      return { success: false, reason: "missing_iban", message };
    }

    let accountHolderName = defaultHolderName;
    if (!accountHolderName && !silent) {
      accountHolderName = window.prompt("Nome do titular da conta:", defaultHolderName) || "";
    }
    if (!accountHolderName) {
      const message = "Nome do titular em falta. Não foi possível submeter o mandato SEPA à Stripe.";
      if (!silent) {
        dispatch(showSnackbar({ message }));
      }
      return { success: false, reason: "missing_holder_name", message };
    }

    const payload = {
      iban: iban.trim(),
      account_holder_name: accountHolderName.trim(),
      email: defaultEmail,
    };

    if (latestMandate?.id) {
      payload.sepa_mandate_id = latestMandate.id;
      if (latestMandate?.mandate_reference) {
        payload.mandate_reference = latestMandate.mandate_reference;
      }
    }

    const response = await callBackendApi(`/contracts/${contractId}/sepa/setup-intent`, "POST", payload);

    if (!response?.success) {
      const errorMessage = getResponseErrorMessage(response, "Falha ao criar mandato SEPA.");
      if (!silent) {
        dispatch(showSnackbar({ message: errorMessage }));
      }
      return { success: false, message: errorMessage };
    }

    if (!silent) {
      dispatch(showSnackbar({ message: "Mandato SEPA processado com sucesso." }));
    }
    if (!skipReload) {
      await reloadContracts();
    }
    return { success: true };
  };

  const revokeSepaMandate = async (row) => {
    if (!row?.id) return;
    setSepaDialogMode("revoke");
    setSepaTargetContract(row);
    setSepaError("");
    setSepaForm({
      iban: "",
      account_holder_name: row?.user?.name || user?.name || "",
      email: row?.user?.email || user?.email || "",
      reason: "",
    });
    setSepaDialogOpen(true);
  };

  const resetSepaDialog = () => {
    setSepaDialogOpen(false);
    setSepaDialogMode("setup");
    setSepaTargetContract(null);
    setSepaSubmitting(false);
    setSepaError("");
    setSepaForm({
      iban: "",
      account_holder_name: "",
      email: "",
      reason: "",
    });
  };

  const updateSepaFormField = (field, value) => {
    setSepaForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitSepaDialog = async () => {
    if (!sepaTargetContract?.id) {
      setSepaError("Contrato inválido.");
      return;
    }

    if (sepaDialogMode === "setup") {
      if (!sepaForm.iban?.trim()) {
        setSepaError("O IBAN é obrigatório.");
        return;
      }

      if (!sepaForm.account_holder_name?.trim()) {
        setSepaError("O nome do titular é obrigatório.");
        return;
      }
    }

    setSepaSubmitting(true);
    setSepaError("");

    const endpoint = sepaDialogMode === "setup"
      ? `/contracts/${sepaTargetContract.id}/sepa/setup-intent`
      : `/contracts/${sepaTargetContract.id}/sepa/revoke`;

    const payload = sepaDialogMode === "setup"
      ? {
          iban: sepaForm.iban.trim(),
          account_holder_name: sepaForm.account_holder_name.trim(),
          email: sepaForm.email.trim(),
        }
      : {
          reason: sepaForm.reason.trim(),
        };

    const response = await callBackendApi(endpoint, "POST", payload);
    setSepaSubmitting(false);

    if (!response?.success) {
      setSepaError(
        response?.error
        || (sepaDialogMode === "setup" ? "Falha ao criar mandato SEPA." : "Falha ao revogar mandato SEPA.")
      );
      return;
    }

    dispatch(showSnackbar({
      message: sepaDialogMode === "setup" ? "Mandato SEPA processado com sucesso." : "Mandato SEPA revogado.",
    }));
    resetSepaDialog();
    reloadContracts();
  };

  const resetSignDialog = () => {
    setSignDialogOpen(false);
    setSignTargetContract(null);
    setSignSubmitting(false);
    setSignError("");
    setSignAcceptedTerms(false);
    setSignSepa(true);
    setSignPreviewLoading(false);
    setSignPreviewError("");
    setSignContractPreviewUrl((prev) => {
      if (prev) window.URL.revokeObjectURL(prev);
      return "";
    });
    setSignSepaPreviewUrl((prev) => {
      if (prev) window.URL.revokeObjectURL(prev);
      return "";
    });
    setSignAdditionalPreviewDocs((prevDocs) => {
      prevDocs.forEach((item) => {
        if (item?.url) window.URL.revokeObjectURL(item.url);
      });
      return [];
    });
    setSignPreviewType("contract");
    isDrawingRef.current = false;
    hasSignatureRef.current = false;
  };

  const resetPreviewDialog = () => {
    setPreviewDialogOpen(false);
    setPreviewTargetContract(null);
    setPreviewLoading(false);
    setPreviewError("");
    if (previewContractUrl) window.URL.revokeObjectURL(previewContractUrl);
    if (previewSepaUrl) window.URL.revokeObjectURL(previewSepaUrl);
    setPreviewAdditionalPreviewDocs((prevDocs) => {
      prevDocs.forEach((item) => {
        if (item?.url) window.URL.revokeObjectURL(item.url);
      });
      return [];
    });
    setPreviewContractUrl("");
    setPreviewSepaUrl("");
    setPreviewType("contract");
  };

  const prepareSignCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = 900;
    canvas.height = 220;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f2740";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    hasSignatureRef.current = false;
  }, []);

  useEffect(() => {
    if (!signDialogOpen) return;
    prepareSignCanvas();
  }, [signDialogOpen, prepareSignCanvas]);

  useEffect(() => {
    if (!signDialogOpen || !signTargetContract?.id) return;

    let cancelled = false;

    const getPreviewBlobUrl = async (documentId) => {
      const response = await callBackendApi(
        `/contract-documents/${documentId}/view`,
        "GET",
        null,
        false,
        { responseType: "blob" }
      );

      if (!response?.success) return null;
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      return window.URL.createObjectURL(blob);
    };

    const pickLatestByType = (docs, type) =>
      (docs || [])
        .filter((doc) => doc?.document_type === type)
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        })[0] || null;

    const loadPreviews = async () => {
      setSignPreviewLoading(true);
      setSignPreviewError("");

      setSignContractPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return "";
      });
      setSignSepaPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return "";
      });
      setSignAdditionalPreviewDocs((prevDocs) => {
        prevDocs.forEach((item) => {
          if (item?.url) window.URL.revokeObjectURL(item.url);
        });
        return [];
      });

      const docsResponse = await callBackendApi(`/contract-documents?contract_id=${signTargetContract.id}`, "GET");
      if (!docsResponse?.success) {
        if (!cancelled) {
          setSignPreviewLoading(false);
          setSignPreviewError("Não foi possível carregar os documentos do contrato.");
        }
        return;
      }

      const docs = Array.isArray(docsResponse.data) ? docsResponse.data : [];
      const contractDoc = pickLatestByType(docs, "contract_terms");
      const sepaDoc = pickLatestByType(docs, "sepa_mandate");
      const otherDocs = (docs || [])
        .filter((doc) => doc?.document_type === "other")
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        });

      const [contractUrl, sepaUrl, ...otherUrls] = await Promise.all([
        contractDoc?.id ? getPreviewBlobUrl(contractDoc.id) : Promise.resolve(null),
        sepaDoc?.id ? getPreviewBlobUrl(sepaDoc.id) : Promise.resolve(null),
        ...otherDocs.map((doc) => (doc?.id ? getPreviewBlobUrl(doc.id) : Promise.resolve(null))),
      ]);

      if (cancelled) {
        if (contractUrl) window.URL.revokeObjectURL(contractUrl);
        if (sepaUrl) window.URL.revokeObjectURL(sepaUrl);
        return;
      }

      setSignContractPreviewUrl(contractUrl || "");
      setSignSepaPreviewUrl(sepaUrl || "");
      const additionalDocs = otherDocs
        .map((doc, idx) => ({
          id: doc?.id,
          label: doc?.file_name || doc?.notes || `Document ${idx + 1}`,
          url: otherUrls[idx] || "",
        }))
        .filter((item) => item.url);
      setSignAdditionalPreviewDocs(additionalDocs);

      setSignPreviewType(
        contractUrl
          ? "contract"
          : (sepaUrl ? "sepa" : (additionalDocs.length > 0 ? "other-0" : "contract"))
      );

      if (!contractUrl && !sepaUrl && additionalDocs.length === 0) {
        setSignPreviewError("Este contrato ainda não tem PDF para visualização.");
      }

      setSignPreviewLoading(false);
    };

    loadPreviews();

    return () => {
      cancelled = true;
    };
  }, [signDialogOpen, signTargetContract]);

  useEffect(() => {
    if (!previewDialogOpen || !previewTargetContract?.id) {
      return undefined;
    }

    let cancelled = false;

    const getPreviewBlobUrl = async (documentId) => {
      const response = await callBackendApi(
        `/contract-documents/${documentId}/view`,
        "GET",
        null,
        false,
        { responseType: "blob" }
      );
      if (!response?.success) return null;
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      return window.URL.createObjectURL(blob);
    };

    const pickLatestByType = (docs, type) => {
      return (docs || [])
        .filter((doc) => doc?.document_type === type)
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        })[0] || null;
    };

    const loadPreviews = async () => {
      setPreviewLoading(true);
      setPreviewError("");
      if (previewContractUrl) window.URL.revokeObjectURL(previewContractUrl);
      if (previewSepaUrl) window.URL.revokeObjectURL(previewSepaUrl);
      setPreviewAdditionalPreviewDocs((prevDocs) => {
        prevDocs.forEach((item) => {
          if (item?.url) window.URL.revokeObjectURL(item.url);
        });
        return [];
      });
      setPreviewContractUrl("");
      setPreviewSepaUrl("");

      const docsResponse = await callBackendApi(`/contract-documents?contract_id=${previewTargetContract.id}`, "GET");
      if (!docsResponse?.success) {
        if (!cancelled) {
          setPreviewLoading(false);
          setPreviewError("Não foi possível carregar os documentos do contrato.");
        }
        return;
      }

      const docs = Array.isArray(docsResponse.data) ? docsResponse.data : [];
      const contractDoc = pickLatestByType(docs, "contract_terms");
      const sepaDoc = pickLatestByType(docs, "sepa_mandate");
      const otherDocs = (docs || [])
        .filter((doc) => doc?.document_type === "other")
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        });

      const [contractUrl, sepaUrl, ...otherUrls] = await Promise.all([
        contractDoc?.id ? getPreviewBlobUrl(contractDoc.id) : Promise.resolve(null),
        sepaDoc?.id ? getPreviewBlobUrl(sepaDoc.id) : Promise.resolve(null),
        ...otherDocs.map((doc) => (doc?.id ? getPreviewBlobUrl(doc.id) : Promise.resolve(null))),
      ]);

      if (cancelled) {
        if (contractUrl) window.URL.revokeObjectURL(contractUrl);
        if (sepaUrl) window.URL.revokeObjectURL(sepaUrl);
        otherUrls.forEach((url) => {
          if (url) window.URL.revokeObjectURL(url);
        });
        return;
      }

      setPreviewContractUrl(contractUrl || "");
      setPreviewSepaUrl(sepaUrl || "");
      const additionalDocs = otherDocs
        .map((doc, idx) => ({
          id: doc?.id,
          label: doc?.file_name || doc?.notes || `Document ${idx + 1}`,
          url: otherUrls[idx] || "",
        }))
        .filter((item) => item.url);
      setPreviewAdditionalPreviewDocs(additionalDocs);
      setPreviewType(
        contractUrl
          ? "contract"
          : (sepaUrl ? "sepa" : (additionalDocs.length > 0 ? "other-0" : "contract"))
      );

      if (!contractUrl && !sepaUrl && additionalDocs.length === 0) {
        setPreviewError("Este contrato ainda não tem PDF para visualização.");
      }

      setPreviewLoading(false);
    };

    loadPreviews();

    return () => {
      cancelled = true;
    };
  }, [previewDialogOpen, previewTargetContract]);

  const clearSignCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f2740";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    hasSignatureRef.current = false;
  };

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const beginDraw = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const point = getCanvasPoint(event);
    isDrawingRef.current = true;
    hasSignatureRef.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const drawMove = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const point = getCanvasPoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const endDraw = () => {
    isDrawingRef.current = false;
  };

  const exportSignatureAsJpeg = React.useCallback((canvas) => {
    if (!canvas) return "";
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const exportCtx = exportCanvas.getContext("2d");
    if (!exportCtx) return "";
    exportCtx.fillStyle = "#ffffff";
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportCtx.drawImage(canvas, 0, 0);
    return exportCanvas.toDataURL("image/jpeg", 0.95);
  }, []);

  const openSignDialog = (row) => {
    setSignTargetContract(row);
    setSignError("");
    setSignAcceptedTerms(false);
    setSignSepa(true);
    setSignDialogOpen(true);
  };

  const openPreviewDialog = (row) => {
    setPreviewTargetContract(row);
    setPreviewError("");
    setPreviewDialogOpen(true);
  };

  const signContractLocal = async () => {
    if (!signTargetContract?.id) {
      setSignError("Contrato inválido.");
      return;
    }
    if (!signAcceptedTerms) {
      setSignError("Tem de aceitar os termos para assinar.");
      return;
    }
    if (!hasSignatureRef.current) {
      setSignError("A assinatura é obrigatória.");
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      setSignError("Não foi possível capturar assinatura.");
      return;
    }

    setSignSubmitting(true);
    setSignError("");
    const signatureDataUrl = exportSignatureAsJpeg(canvas);
    if (!signatureDataUrl) {
      setSignSubmitting(false);
      setSignError("Não foi possível exportar assinatura.");
      return;
    }
    const response = await callBackendApi(`/contracts/${signTargetContract.id}/sign-local`, "POST", {
      accepted_terms: true,
      sign_sepa: signSepa,
      signature_data_url: signatureDataUrl,
      device_info: navigator?.userAgent || "",
    });
    setSignSubmitting(false);

    if (!response?.success) {
      setSignError(response?.error || "Falha ao assinar localmente.");
      return;
    }

    let autoSetupResult = null;
    if (signSepa) {
      autoSetupResult = await setupSepaMandate(
        { ...signTargetContract, signature_date: new Date().toISOString().slice(0, 10) },
        { silent: true, skipSignatureCheck: true, skipReload: true }
      );
    }

    if (signSepa && autoSetupResult?.success) {
      dispatch(showSnackbar({ message: "Contrato assinado e mandato SEPA submetido automaticamente para Stripe." }));
    } else if (signSepa && !autoSetupResult?.success) {
      dispatch(showSnackbar({
        message: autoSetupResult?.message
          ? `Contrato assinado. ${autoSetupResult.message}`
          : "Contrato assinado. Submissão automática SEPA para Stripe não concluída. Verifique IBAN/titular e execute Setup SEPA.",
      }));
    } else {
      dispatch(showSnackbar({ message: "Contrato assinado localmente." }));
    }
    resetSignDialog();
    reloadContracts();
  };

  const buildInlineContractResources = (contractId) => {
    const keys = [
      "contract-lines",
      "contract-payers",
      "contract-line-classes",
      "contract-installments",
      "contract-installment-payments",
      "contract-debts",
      "contract-documents",
      "contract-signatures",
      "sepa-mandates",
      "contract-events",
    ];

    return keys
      .map((key) => contractResources.find((resource) => resource.key === key))
      .filter(Boolean)
      .map((resource) => {
        const endpointHasQuery = resource.endpoint.includes("?");
        const endpoint = `${resource.endpoint}${endpointHasQuery ? "&" : "?"}contract_id=${contractId}`;
        const formFields = (resource.formFields || []).map((field) => {
          if (field.name === "contract_id") {
            return { ...field, defaultValue: contractId };
          }
          return field;
        });
        return {
          ...resource,
          endpoint,
          formFields,
        };
      });
  };

  const renderContractsTab = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Contracts</Typography>
          <Button variant="contained" onClick={openCreateContractDialog}>
            New Contract
          </Button>
        </Box>
        {contractsError ? <Alert severity="warning" sx={{ mb: 2 }}>{contractsError}</Alert> : null}
        {contractsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contract Number</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Mandate</TableCell>
                <TableCell>Mandate Ref</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Periodicity</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(contracts || []).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{toDisplay(row.contract_number)}</TableCell>
                  <TableCell>{toDisplay(row?.plan?.name || row.plan_id)}</TableCell>
                  <TableCell>{toDisplay(row.status)}</TableCell>
                  <TableCell>{toDisplay(row.mandate_status)}</TableCell>
                  <TableCell>{toDisplay(row.mandate_reference)}</TableCell>
                  <TableCell>{toDisplay(row.start_date)}</TableCell>
                  <TableCell>{toDisplay(row.contract_total)}</TableCell>
                  <TableCell>{toDisplay(row.periodicity)}</TableCell>
                  <TableCell>{toDisplay(row.currency)}</TableCell>
                  <TableCell>
                    <Button size="small" color="info" onClick={() => openPreviewDialog(row)}>
                      Preview Docs
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setupSepaMandate(row)}
                      disabled={!Boolean(row?.signature_date)}
                      title={!Boolean(row?.signature_date) ? "Assine o contrato primeiro." : ""}
                    >
                      Setup SEPA
                    </Button>
                    {row?.mandate_status === "active" ? (
                      <Button size="small" color="warning" onClick={() => revokeSepaMandate(row)}>
                        Revoke SEPA
                      </Button>
                    ) : null}
                    {!Boolean(row?.signature_date) ? (
                      <Button size="small" color="success" onClick={() => openSignDialog(row)}>
                        Sign Local
                      </Button>
                    ) : null}
                    <IconButton size="small" color="primary" onClick={() => openEditContractDialog(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => deleteContract(row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {contracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary">
                      No contracts found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}

      </CardContent>

      <Dialog open={contractDialogOpen} onClose={closeContractDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingContract ? "Edit Contract" : "Create Contract"}</DialogTitle>
        <DialogContent>
          <ContractFormFields
            form={contractForm}
            setForm={setContractForm}
            contractPlans={contractPlans}
          />

          {editingContract ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Related Information</Typography>
              {(() => {
                const inlineResources = buildInlineContractResources(editingContract.id);
                const selectedResource = inlineResources[contractInlineTab] || inlineResources[0];
                return (
                  <>
                    <Tabs
                      value={contractInlineTab}
                      onChange={(_, next) => setContractInlineTab(next)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ mb: 2 }}
                    >
                      {inlineResources.map((resource) => (
                        <Tab key={`contract-inline-${resource.key}`} label={resource.label} />
                      ))}
                    </Tabs>

                    {selectedResource ? (
                      <ResourceCrudPanel
                        resource={selectedResource}
                        resourceLookup={Object.fromEntries(inlineResources.map((resource) => [resource.key, resource]))}
                      />
                    ) : (
                      <Alert severity="info">No related resources available.</Alert>
                    )}
                  </>
                );
              })()}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              Save the contract first to manage related information.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContractDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveContract} disabled={contractSaving}>
            {contractSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <LocalContractSignDialog
        open={signDialogOpen}
        onClose={resetSignDialog}
        contractLabel={signTargetContract?.contract_number || signTargetContract?.id}
        loading={signPreviewLoading}
        error={signError}
        previewError={signPreviewError}
        previewType={signPreviewType}
        onPreviewTypeChange={setSignPreviewType}
        contractUrl={signContractPreviewUrl}
        sepaUrl={signSepaPreviewUrl}
        additionalDocs={signAdditionalPreviewDocs}
        canvasRef={canvasRef}
        onBeginDraw={beginDraw}
        onDrawMove={drawMove}
        onEndDraw={endDraw}
        onClearSignature={clearSignCanvas}
        acceptedTerms={signAcceptedTerms}
        onAcceptedTermsChange={setSignAcceptedTerms}
        signSepa={signSepa}
        onSignSepaChange={setSignSepa}
        submitting={signSubmitting}
        onSubmit={signContractLocal}
      />

      <ContractDocumentsPreviewDialog
        open={previewDialogOpen}
        onClose={resetPreviewDialog}
        contractLabel={previewTargetContract?.contract_number || previewTargetContract?.id}
        loading={previewLoading}
        error={previewError}
        previewType={previewType}
        onPreviewTypeChange={setPreviewType}
        contractUrl={previewContractUrl}
        sepaUrl={previewSepaUrl}
        additionalDocs={previewAdditionalPreviewDocs}
      />

      <SepaMandateDialog
        open={sepaDialogOpen}
        mode={sepaDialogMode}
        contractLabel={sepaTargetContract?.contract_number || sepaTargetContract?.id}
        form={sepaForm}
        onChange={updateSepaFormField}
        submitting={sepaSubmitting}
        error={sepaError}
        onClose={resetSepaDialog}
        onSubmit={submitSepaDialog}
      />
    </Card>
  );

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );

  const emp = user.gym_user_info;
  const contact = user.contact;
  const marketing = user.marketing_info;
  const medical = user.medical_info;

  return (
    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", mt: 4 }}>
      <Card className="my-5">
        <CardContent>
          <Grid container>
            <Grid item xs={1}>
              <IconButton onClick={() => window.history.back()} color="primary">
                <ArrowBackIcon />
              </IconButton>
            </Grid>
            <Grid item xs={11}>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {id ? "Edit User" : "Create User"}{" "}
                {user.name ? ` - ${user.name}` : ""}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {id ? (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: "8px !important" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button variant="outlined" onClick={() => navigate(`/dashboard/users/gym-user/${id}/agenda`)}>
                Open Agenda
              </Button>
            </Box>
            <Tabs value={activeTab} onChange={(_, next) => setActiveTab(next)}>
              <Tab label="Profile" />
              <Tab label="Related Information" />
              <Tab label="Contracts" />
            </Tabs>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 1 ? renderRelatedTab() : activeTab === 2 ? renderContractsTab() : (
      <Card>
        <CardContent>
          {/* Account Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Account Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              {renderSelect(
                "Title",
                "gym_user_info",
                "title_id",
                lookups.titles || []
              )}
            </Grid>
            <Grid item xs={8}>
              <TextField
                label="Name"
                fullWidth
                value={user.name}
                onChange={(e) => handleChange("root", "name", e.target.value)}
                helperText={getError(errors, "name")}
                error={!!getError(errors, "name")}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                label={formatLabel("address")}
                fullWidth
                value={contact["address"] || ""}
                onChange={(e) =>
                  handleChange("contact", "address", e.target.value)
                }
                helperText={getError(errors, `contact.address`)}
                error={!!getError(errors, `contact.address`)}
              />
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Country",
                "contact",
                "country_id",
                lookups.countries || []
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("city")}
                fullWidth
                value={contact["city"] || ""}
                onChange={(e) =>
                  handleChange("contact", "city", e.target.value)
                }
                helperText={getError(errors, `contact.city`)}
                error={!!getError(errors, `contact.city`)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("postal_code")}
                fullWidth
                value={contact["postal_code"] || ""}
                onChange={(e) =>
                  handleChange("contact", "postal_code", e.target.value)
                }
                helperText={getError(errors, `contact.postal_code`)}
                error={!!getError(errors, `contact.postal_code`)}
              />
            </Grid>
            {["phone", "mobile", "emergency_contact"].map((f) => (
              <Grid item xs={4} key={f}>
                <TextField
                  label={formatLabel(f)}
                  fullWidth
                  value={contact[f] || ""}
                  onChange={(e) => handleChange("contact", f, e.target.value)}
                  helperText={getError(errors, `contact.${f}`)}
                  error={!!getError(errors, `contact.${f}`)}
                />
              </Grid>
            ))}
            <Grid item xs={6}>
              <TextField
                label="Email"
                fullWidth
                value={user.email}
                onChange={(e) => handleChange("root", "email", e.target.value)}
                helperText={getError(errors, "email")}
                error={!!getError(errors, "email")}
              />
            </Grid>
            <Grid item xs={6}>
              {renderSelect("Role", "root", "role", UserRoleOptions)}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Password"
                fullWidth
                type="password"
                value={user.password}
                onChange={(e) =>
                  handleChange("root", "password", e.target.value)
                }
                helperText={getError(errors, "password")}
                error={!!getError(errors, "password")}
              />
            </Grid>
            <Grid item xs={6}>
              {renderSelect(
                "Gender",
                "gym_user_info",
                "gender_id",
                lookups.genders || []
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("birth_date")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={emp["birth_date"] || ""}
                onChange={(e) =>
                  handleChange("gym_user_info", "birth_date", e.target.value)
                }
                helperText={getError(errors, `gym_user_info.birth_date`)}
                error={!!getError(errors, `gym_user_info.birth_date`)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={formatLabel("nif")}
                fullWidth
                value={emp["nif"] || ""}
                onChange={(e) =>
                  handleChange("gym_user_info", "nif", e.target.value)
                }
                helperText={getError(errors, `gym_user_info.nif`)}
                error={!!getError(errors, `gym_user_info.nif`)}
              />
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Document Type",
                "gym_user_info",
                "document_type_id",
                lookups.document_types || []
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={formatLabel("document_number")}
                fullWidth
                value={emp["document_number"] || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "document_number",
                    e.target.value
                  )
                }
                helperText={getError(errors, `gym_user_info.document_number`)}
                error={!!getError(errors, `gym_user_info.document_number`)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={formatLabel("document_expiry")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={emp["document_expiry"] || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "document_expiry",
                    e.target.value
                  )
                }
                helperText={getError(errors, `gym_user_info.document_expiry`)}
                error={!!getError(errors, `gym_user_info.document_expiry`)}
              />
            </Grid>
            <Grid item xs={6}>
              {renderSelect(
                "Nationality",
                "gym_user_info",
                "nationality_id",
                lookups.countries || []
              )}
            </Grid>
            <Grid item xs={6}>
              {renderSelect(
                "Civil State",
                "gym_user_info",
                "civil_status_id",
                lookups.civil_statuses || []
              )}
            </Grid>

            {/* Father / Mother info (CONTACT) */}
            <Grid item xs={8}>
              <TextField
                label={formatLabel("father_name")}
                fullWidth
                value={contact["father_name"] || ""}
                onChange={(e) =>
                  handleChange("contact", "father_name", e.target.value)
                }
                helperText={getError(errors, `contact.father_name`)}
                error={!!getError(errors, `contact.father_name`)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={formatLabel("father_contact")}
                fullWidth
                value={contact["father_contact"] || ""}
                onChange={(e) =>
                  handleChange("contact", "father_contact", e.target.value)
                }
                helperText={getError(errors, `contact.father_contact`)}
                error={!!getError(errors, `contact.father_contact`)}
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                label={formatLabel("mother_name")}
                fullWidth
                value={contact["mother_name"] || ""}
                onChange={(e) =>
                  handleChange("contact", "mother_name", e.target.value)
                }
                helperText={getError(errors, `contact.mother_name`)}
                error={!!getError(errors, `contact.mother_name`)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={formatLabel("mother_contact")}
                fullWidth
                value={contact["mother_contact"] || ""}
                onChange={(e) =>
                  handleChange("contact", "mother_contact", e.target.value)
                }
                helperText={getError(errors, `contact.mother_contact`)}
                error={!!getError(errors, `contact.mother_contact`)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Marketing Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Marketing Preferences
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("last_contact")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={marketing["last_contact"] || ""}
                onChange={(e) =>
                  handleChange("marketing_info", "last_contact", e.target.value)
                }
                helperText={getError(errors, `marketing_info.last_contact`)}
                error={!!getError(errors, `marketing_info.last_contact`)}
              />
            </Grid>

            <Grid item xs={6}>
              {renderSelect(
                "Actual Action",
                "marketing_info",
                "actual_action",
                lookups.marketing_actions || []
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("next_contact")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={marketing["next_contact"] || ""}
                onChange={(e) =>
                  handleChange("marketing_info", "next_contact", e.target.value)
                }
                helperText={getError(errors, `marketing_info.next_contact`)}
                error={!!getError(errors, `marketing_info.next_contact`)}
              />
            </Grid>

            <Grid item xs={6}>
              {renderSelect(
                "Next Action",
                "marketing_info",
                "next_action",
                lookups.marketing_actions || []
              )}
            </Grid>

            <Grid item xs={6}>
              {renderSelect(
                "Manager",
                "gym_user_info",
                "manager_id",
                employees || []
              )}
            </Grid>
            <Grid item xs={6}>
              {renderSelect(
                "Professor",
                "gym_user_info",
                "professor_id",
                employees || []
              )}
            </Grid>
            <Grid item xs={6}>
              {renderSelect(
                "Campaign",
                "marketing_info",
                "campgain_id",
                lookups.marketing_campgains || []
              )}
            </Grid>
            <Grid item xs={6}>
              {renderSelect(
                "Source",
                "marketing_info",
                "source_id",
                lookups.marketing_sources || []
              )}
            </Grid>
            {Object.entries({
              receive_sms: "Receive SMS",
              receive_email: "Receive Email",
              allow_marketing: "Allow Marketing",
              image_consent: "Image Consent",
              show_in_program: "Show In Program",
              show_in_app: "Show In App",
            }).map(([f, label]) => (
              <Grid item xs={3} key={f}>
                {renderSwitch(label, "marketing_info", f, marketing[f])}
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                label="Interests"
                fullWidth
                multiline
                rows={2}
                value={marketing.interests || ""}
                onChange={(e) =>
                  handleChange("marketing_info", "interests", e.target.value)
                }
                helperText={getError(errors, "marketing_info.interests")}
                error={!!getError(errors, "marketing_info.interests")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Medical / Internal Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Internal Processing
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!medical.insurance}
                    onChange={(e) =>
                      handleChange(
                        "medical_info",
                        "insurance",
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                }
                label="Has Insurance"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!medical.medical_authorization}
                    onChange={(e) =>
                      handleChange(
                        "medical_info",
                        "medical_authorization",
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                }
                label="Medical Authorization"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={formatLabel("start_date")}
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={emp["start_date"] || ""}
                onChange={(e) =>
                  handleChange("gym_user_info", "start_date", e.target.value)
                }
                helperText={getError(errors, `gym_user_info.start_date`)}
                error={!!getError(errors, `gym_user_info.start_date`)}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!emp.is_athlete}
                    onChange={(e) =>
                      handleChange(
                        "gym_user_info",
                        "is_athlete",
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                }
                label="Athlete"
              />
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Modality",
                "gym_user_info",
                "athlete_modality",
                lookups.document_types || [] // trocar para lookup correto quando tiveres
              )}
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={formatLabel("invoicing_nif")}
                type="text"
                fullWidth
                value={emp["invoicing_nif"] || ""}
                onChange={(e) =>
                  handleChange("gym_user_info", "invoicing_nif", e.target.value)
                }
                helperText={getError(errors, `gym_user_info.invoicing_nif`)}
                error={!!getError(errors, `gym_user_info.invoicing_nif`)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={formatLabel("invoicing_name")}
                type="text"
                fullWidth
                value={emp["invoicing_name"] || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "invoicing_name",
                    e.target.value
                  )
                }
                helperText={getError(errors, `gym_user_info.invoicing_name`)}
                error={!!getError(errors, `gym_user_info.invoicing_name`)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Invoicing Address"
                fullWidth
                multiline
                rows={2}
                value={emp.invoicing_address || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "invoicing_address",
                    e.target.value
                  )
                }
                helperText={getError(errors, "gym_user_info.invoicing_address")}
                error={!!getError(errors, "gym_user_info.invoicing_address")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Employer Info
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Company Name"
                rows={2}
                value={emp.job_company_name || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "job_company_name",
                    e.target.value
                  )
                }
                helperText={getError(errors, "gym_user_info.job_company_name")}
                error={!!getError(errors, "gym_user_info.job_company_name")}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Company Phone"
                rows={2}
                value={emp.job_company_phone || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "job_company_phone",
                    e.target.value
                  )
                }
                helperText={getError(errors, "gym_user_info.job_company_phone")}
                error={!!getError(errors, "gym_user_info.job_company_phone")}
              />
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Country",
                "gym_user_info",
                "job_employement_country",
                lookups.countries || [] // trocar para lookup correto quando tiveres
              )}
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Department",
                "gym_user_info",
                "job_department_id",
                lookups.departments || [] // trocar para lookup correto quando tiveres
              )}
            </Grid>
            <Grid item xs={4}>
              {renderSelect(
                "Position",
                "gym_user_info",
                "job_position_id",
                lookups.job_positions || [] // trocar para lookup correto quando tiveres
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Profession"
                rows={2}
                value={emp.job_profession || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "job_profession",
                    e.target.value
                  )
                }
                helperText={getError(errors, "gym_user_info.job_profession")}
                error={!!getError(errors, "gym_user_info.job_profession")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                multiline
                rows={2}
                value={emp.job_company_address || ""}
                onChange={(e) =>
                  handleChange(
                    "gym_user_info",
                    "job_company_address",
                    e.target.value
                  )
                }
                helperText={getError(
                  errors,
                  "gym_user_info.job_company_address"
                )}
                error={!!getError(errors, "gym_user_info.job_company_address")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Observations
          </Typography>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                label="Obs"
                fullWidth
                multiline
                rows={2}
                value={emp.notes || ""}
                onChange={(e) =>
                  handleChange("gym_user_info", "notes", e.target.value)
                }
                helperText={getError(errors, "gym_user_info.notes")}
                error={!!getError(errors, "gym_user_info.notes")}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Relationships
          </Typography>
          <Grid container spacing={2}>
            {createdRelationships.map((rel, index) => (
              <React.Fragment key={rel.id || index}>
                {/* Connection */}
                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    label="Connection"
                    value={rel.connection || ""}
                    onChange={(e) =>
                      handleRelationshipChange(
                        index,
                        "connection",
                        e.target.value
                      )
                    }
                    helperText={getError(
                      errors,
                      `created_relationships.${index}.connection`
                    )}
                    error={
                      !!getError(
                        errors,
                        `created_relationships.${index}.connection`
                      )
                    }
                  >
                    <MenuItem value="">-- Select --</MenuItem>
                    {relationshipOptions().map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Related User */}
                <Grid item xs={4}>
                  <TextField
                    select
                    fullWidth
                    label="Related User"
                    value={rel.related_user_id || ""}
                    onChange={(e) =>
                      handleRelationshipChange(
                        index,
                        "related_user_id",
                        e.target.value
                      )
                    }
                    helperText={getError(
                      errors,
                      `created_relationships.${index}.related_user_id`
                    )}
                    error={
                      !!getError(
                        errors,
                        `created_relationships.${index}.related_user_id`
                      )
                    }
                  >
                    <MenuItem value="">-- Select --</MenuItem>
                    {gymUsersOptions.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name} {u.email ? `(${u.email})` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Dates */}
                <Grid item xs={2}>
                  <TextField
                    label="Starting Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={rel.starting_date || ""}
                    onChange={(e) =>
                      handleRelationshipChange(
                        index,
                        "starting_date",
                        e.target.value
                      )
                    }
                    helperText={getError(
                      errors,
                      `created_relationships.${index}.starting_date`
                    )}
                    error={
                      !!getError(
                        errors,
                        `created_relationships.${index}.starting_date`
                      )
                    }
                  />
                </Grid>

                <Grid item xs={2}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={rel.end_date || ""}
                    onChange={(e) =>
                      handleRelationshipChange(
                        index,
                        "end_date",
                        e.target.value
                      )
                    }
                    helperText={getError(
                      errors,
                      `created_relationships.${index}.end_date`
                    )}
                    error={
                      !!getError(
                        errors,
                        `created_relationships.${index}.end_date`
                      )
                    }
                  />
                </Grid>

                {/* Delete */}
                <Grid
                  item
                  xs={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRelationshipDelete(index)}
                  >
                    Delete
                  </Button>
                </Grid>
              </React.Fragment>
            ))}

            <Grid item xs={12}>
              <Button variant="outlined" onClick={addRelationship}>
                + Add Relationship
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: "right" }}>
            <Button
              type="button"
              fullWidth={false}
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : id ? "💾 Save Changes" : "➕ Create User"}
            </Button>
          </Box>
        </CardContent>
      </Card>
      )}
    </Box>
  );
};

export default UserProfile;
